import React, { useState, useEffect } from 'react';
import { FiSave, FiEdit, FiTrash2, FiClock, FiTrendingUp, FiPlus, FiSettings } from 'react-icons/fi';
import api from '../services/api';
import Modal from '../components/Modal';
import '../components/Button.css';
import './Parameters.css';

const Parameters = () => {
  const [leveranciers, setLeveranciers] = useState([]);
  const [parameters, setParameters] = useState([]);
  const [allParameters, setAllParameters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLeverancierModal, setShowLeverancierModal] = useState(false);
  const [showParameterModal, setShowParameterModal] = useState(false);
  const [editingLeverancier, setEditingLeverancier] = useState(null);
  const [editingParameter, setEditingParameter] = useState(null);
  const [levertijdForm, setLevertijdForm] = useState({ levertijd_dagen: '' });
  const [verkoopgroeiForm, setVerkoopgroeiForm] = useState({ waarde: '' });
  const [companyForm, setCompanyForm] = useState({
    company_name: '',
    company_address: '',
    company_email: '',
    company_phone: '',
    company_website: ''
  });
  const [parameterForm, setParameterForm] = useState({ parameter_naam: '', waarde: '', beschrijving: '' });

  useEffect(() => {
    fetchLeveranciers();
    fetchParameters();
  }, []);

  const fetchLeveranciers = async () => {
    try {
      const response = await api.get('/leveranciers');
      setLeveranciers(response.data);
    } catch (error) {
      console.error('Error fetching leveranciers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchParameters = async () => {
    try {
      const response = await api.get('/parameters');
      const params = {};
      response.data.forEach(p => {
        params[p.parameter_naam] = p.waarde;
      });
      setParameters(params);
      setAllParameters(response.data);
      
      // Set verkoopgroei form value if exists
      if (params.verkoopgroei_percentage) {
        setVerkoopgroeiForm({ waarde: params.verkoopgroei_percentage });
      }

      setCompanyForm({
        company_name: params.company_name || '',
        company_address: params.company_address || '',
        company_email: params.company_email || '',
        company_phone: params.company_phone || '',
        company_website: params.company_website || ''
      });
    } catch (error) {
      console.error('Error fetching parameters:', error);
    }
  };

  const handleLevertijdSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/leveranciers/${editingLeverancier.id}`, {
        ...editingLeverancier,
        levertijd_dagen: parseInt(levertijdForm.levertijd_dagen) || null
      });
      setShowLeverancierModal(false);
      setEditingLeverancier(null);
      setLevertijdForm({ levertijd_dagen: '' });
      fetchLeveranciers();
    } catch (error) {
      console.error('Error updating leverancier:', error);
      alert('Fout bij opslaan: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleVerkoopgroeiSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/parameters', {
        parameter_naam: 'verkoopgroei_percentage',
        waarde: verkoopgroeiForm.waarde,
        beschrijving: 'Verwachte verkoopgroei in procenten'
      });
      alert('Verkoopgroei opgeslagen!');
      fetchParameters();
    } catch (error) {
      console.error('Error saving parameter:', error);
      alert('Fout bij opslaan: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    try {
      const entries = [
        { parameter_naam: 'company_name', waarde: companyForm.company_name, beschrijving: 'Bedrijfsnaam' },
        { parameter_naam: 'company_address', waarde: companyForm.company_address, beschrijving: 'Bedrijfsadres' },
        { parameter_naam: 'company_email', waarde: companyForm.company_email, beschrijving: 'Bedrijfsemail' },
        { parameter_naam: 'company_phone', waarde: companyForm.company_phone, beschrijving: 'Telefoonnummer' },
        { parameter_naam: 'company_website', waarde: companyForm.company_website, beschrijving: 'Website' }
      ];
      await Promise.all(entries.map(entry => api.post('/parameters', entry)));
      alert('Bedrijfsgegevens opgeslagen!');
      fetchParameters();
    } catch (error) {
      console.error('Error saving company info:', error);
      alert('Fout bij opslaan: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEditLevertijd = (leverancier) => {
    setEditingLeverancier(leverancier);
    setLevertijdForm({ levertijd_dagen: leverancier.levertijd_dagen || '' });
    setShowLeverancierModal(true);
  };

  const handleAddParameter = () => {
    setEditingParameter(null);
    setParameterForm({ parameter_naam: '', waarde: '', beschrijving: '' });
    setShowParameterModal(true);
  };

  const handleEditParameter = (param) => {
    setEditingParameter(param);
    setParameterForm({
      parameter_naam: param.parameter_naam,
      waarde: param.waarde || '',
      beschrijving: param.beschrijving || ''
    });
    setShowParameterModal(true);
  };

  const handleDeleteParameter = async (parameterNaam) => {
    if (!window.confirm(`Weet je zeker dat je parameter "${parameterNaam}" wilt verwijderen?`)) {
      return;
    }
    
    try {
      await api.delete(`/parameters/${parameterNaam}`);
      alert('Parameter verwijderd!');
      fetchParameters();
    } catch (error) {
      console.error('Error deleting parameter:', error);
      alert('Fout bij verwijderen: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleParameterSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/parameters', parameterForm);
      setShowParameterModal(false);
      setEditingParameter(null);
      setParameterForm({ parameter_naam: '', waarde: '', beschrijving: '' });
      alert('Parameter opgeslagen!');
      fetchParameters();
    } catch (error) {
      console.error('Error saving parameter:', error);
      alert('Fout bij opslaan: ' + (error.response?.data?.error || error.message));
    }
  };

  if (loading) {
    return <div className="loading">Laden...</div>;
  }

  const inkoopvoorstelParameters = allParameters.filter((p) => p.parameter_naam.startsWith('inkoopvoorstel_'));
  const overigeParameters = allParameters.filter((p) => !p.parameter_naam.startsWith('inkoopvoorstel_'));

  return (
    <div className="parameters">
      <div className="page-header">
        <h1 className="page-title">Parameters</h1>
        <p className="page-subtitle">Configureer leverancier levertijden en verkoopgroei</p>
      </div>

      <div className="parameters-container">
        {/* Bedrijfsgegevens */}
        <div className="parameter-section">
          <div className="section-header">
            <FiSettings className="section-icon" />
            <h2>Bedrijfsgegevens (voor PDF en koptekst)</h2>
          </div>
          <p className="section-description">
            Vul de basisgegevens van je bedrijf in. Deze worden gebruikt in PDF-documenten.
          </p>
          <form onSubmit={handleCompanySubmit} className="parameter-form">
            <div className="form-group">
              <label>Bedrijfsnaam</label>
              <input
                type="text"
                value={companyForm.company_name}
                onChange={(e) => setCompanyForm({ ...companyForm, company_name: e.target.value })}
                placeholder="Bedrijfsnaam"
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Adres</label>
              <input
                type="text"
                value={companyForm.company_address}
                onChange={(e) => setCompanyForm({ ...companyForm, company_address: e.target.value })}
                placeholder="Straat + nr, postcode plaats"
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={companyForm.company_email}
                onChange={(e) => setCompanyForm({ ...companyForm, company_email: e.target.value })}
                placeholder="info@jouwbedrijf.nl"
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Telefoon</label>
              <input
                type="text"
                value={companyForm.company_phone}
                onChange={(e) => setCompanyForm({ ...companyForm, company_phone: e.target.value })}
                placeholder="+31 ..."
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Website</label>
              <input
                type="text"
                value={companyForm.company_website}
                onChange={(e) => setCompanyForm({ ...companyForm, company_website: e.target.value })}
                placeholder="www.jouwbedrijf.nl"
                className="form-input"
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                <FiSave /> Opslaan
              </button>
            </div>
          </form>
        </div>

        {/* Leverancier Levertijden Sectie */}
        <div className="parameter-section">
          <div className="section-header">
            <FiClock className="section-icon" />
            <h2>Levertijden Leveranciers</h2>
          </div>
          <p className="section-description">
            Stel de verwachte levertijd in dagen in voor elke leverancier.
          </p>

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Leverancier</th>
                  <th>Contactpersoon</th>
                  <th>Email</th>
                  <th>Levertijd (dagen)</th>
                  <th>Acties</th>
                </tr>
              </thead>
              <tbody>
                {leveranciers.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="empty-state">
                      Geen leveranciers gevonden. Voeg eerst leveranciers toe in het "Leveranciers" tabblad.
                    </td>
                  </tr>
                ) : (
                  leveranciers.map((leverancier) => (
                    <tr key={leverancier.id}>
                      <td><strong>{leverancier.naam}</strong></td>
                      <td>{leverancier.contactpersoon || '-'}</td>
                      <td>{leverancier.email || '-'}</td>
                      <td>
                        {leverancier.levertijd_dagen ? (
                          <span className="levertijd-badge">{leverancier.levertijd_dagen} dagen</span>
                        ) : (
                          <span className="levertijd-badge not-set">Niet ingesteld</span>
                        )}
                      </td>
                      <td>
                        <button
                          className="btn-icon"
                          onClick={() => handleEditLevertijd(leverancier)}
                          title="Bewerk levertijd"
                        >
                          <FiEdit />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Verkoopgroei Sectie */}
        <div className="parameter-section">
          <div className="section-header">
            <FiTrendingUp className="section-icon" />
            <h2>Verwachte Verkoopgroei</h2>
          </div>
          <p className="section-description">
            Stel de verwachte verkoopgroei in procenten in voor prognoses en planning.
          </p>

          <form onSubmit={handleVerkoopgroeiSubmit} className="parameter-form">
            <div className="form-group">
              <label>Verwachte Verkoopgroei (%)</label>
              <div className="input-with-button">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1000"
                  value={verkoopgroeiForm.waarde}
                  onChange={(e) => setVerkoopgroeiForm({ waarde: e.target.value })}
                  placeholder="Bijv. 15.5 voor 15.5% groei"
                  className="form-input"
                />
                <button type="submit" className="btn btn-primary">
                  <FiSave /> Opslaan
                </button>
              </div>
              {parameters.verkoopgroei_percentage && (
                <p className="current-value">
                  Huidige waarde: <strong>{parameters.verkoopgroei_percentage}%</strong>
                </p>
              )}
            </div>
          </form>
        </div>

        {/* Inkoopvoorstel Parameters Sectie */}
        <div className="parameter-section">
          <div className="section-header">
            <FiSettings className="section-icon" />
            <h2>Inkoopvoorstel Parameters</h2>
            <button
              className="btn btn-primary"
              onClick={handleAddParameter}
              style={{ marginLeft: 'auto' }}
            >
              <FiPlus /> Nieuwe Parameter
            </button>
          </div>
          <p className="section-description">
            Configureer de voorwaarden voor het automatische inkoopvoorstel. Parameters die beginnen met "inkoopvoorstel_" worden gebruikt in de berekening.
          </p>

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Parameter Naam</th>
                  <th>Waarde</th>
                  <th>Beschrijving</th>
                  <th className="parameters-actions-col">Acties</th>
                </tr>
              </thead>
              <tbody>
                {inkoopvoorstelParameters.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="empty-state">
                      Geen inkoopvoorstel parameters. Klik op "Nieuwe Parameter" om er een toe te voegen.
                    </td>
                  </tr>
                ) : (
                  inkoopvoorstelParameters.map((param) => (
                      <tr key={param.id}>
                        <td><strong>{param.parameter_naam}</strong></td>
                        <td>{param.waarde || <span style={{ color: '#999' }}>Leeg</span>}</td>
                        <td>{param.beschrijving || <span style={{ color: '#999' }}>-</span>}</td>
                        <td className="parameters-actions-col">
                          <button
                            className="btn-icon"
                            onClick={() => handleEditParameter(param)}
                            title="Bewerken"
                          >
                            <FiEdit />
                          </button>
                          <button
                            className="btn-icon"
                            onClick={() => handleDeleteParameter(param.parameter_naam)}
                            title="Verwijderen"
                            style={{ color: '#ff4444', marginLeft: '0.5rem' }}
                          >
                            <FiTrash2 />
                          </button>
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>

          <div className="parameter-help">
            <h4>Voorbeeld Parameters:</h4>
            <ul>
              <li><strong>inkoopvoorstel_buffer_dagen</strong> - Aantal extra dagen buffer naast levertijd (default: 14)</li>
              <li><strong>inkoopvoorstel_buffer_percentage</strong> - Percentage extra buffer op voorgesteld aantal (default: 10)</li>
              <li><strong>inkoopvoorstel_max_buffer_stuks</strong> - Maximum aantal stuks buffer (default: 20)</li>
              <li><strong>inkoopvoorstel_minimum_buffer_dagen</strong> - Buffer dagen voor minimum voorraad (default: 7)</li>
            </ul>
          </div>
        </div>

        <div className="parameter-section">
          <div className="section-header">
            <FiSettings className="section-icon" />
            <h2>Overige Parameters</h2>
          </div>
          <p className="section-description">
            Hier staan alle overige parameters, o.a. Shopify levertijd: `shopify_delivery_cutoff_hour`,{' '}
            `shopify_delivery_country_transit_days`, en <strong>vakantie</strong>: <code>shopify_delivery_closed_dates</code> (JSON, zie SHOPIFY_DELIVERY_WIDGET.md).{' '}
            <strong>Bewerken:</strong> klik op het potlood in de kolom &quot;Acties&quot; (scroll op smalle schermen naar rechts in de tabel).
            Voor Shopify-orderimport &amp; API-keys: gebruik liever{' '}
            <strong>Koppelingen</strong> (daar worden geheimen gemaskeerd en JSON gecontroleerd).
          </p>
          <div className="section-header" style={{ marginTop: '0.5rem', marginBottom: '0.75rem' }}>
            <button type="button" className="btn btn-primary" onClick={handleAddParameter} style={{ marginLeft: 0 }}>
              <FiPlus /> Nieuwe parameter (overig)
            </button>
          </div>

          <div className="table-container">
            <table className="data-table parameters-overige-table">
              <thead>
                <tr>
                  <th>Parameter Naam</th>
                  <th>Waarde</th>
                  <th>Beschrijving</th>
                  <th className="parameters-actions-col">Acties</th>
                </tr>
              </thead>
              <tbody>
                {overigeParameters.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="empty-state">
                      Geen overige parameters gevonden.
                    </td>
                  </tr>
                ) : (
                  overigeParameters.map((param) => (
                    <tr key={param.id}>
                      <td><strong>{param.parameter_naam}</strong></td>
                      <td>{param.waarde || <span style={{ color: '#999' }}>Leeg</span>}</td>
                      <td>{param.beschrijving || <span style={{ color: '#999' }}>-</span>}</td>
                      <td className="parameters-actions-col">
                        <button
                          className="btn-icon"
                          onClick={() => handleEditParameter(param)}
                          title="Bewerken"
                        >
                          <FiEdit />
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => handleDeleteParameter(param.parameter_naam)}
                          title="Verwijderen"
                          style={{ color: '#ff4444', marginLeft: '0.5rem' }}
                        >
                          <FiTrash2 />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Levertijd Edit Modal */}
      <Modal
        show={showLeverancierModal}
        onClose={() => {
          setShowLeverancierModal(false);
          setEditingLeverancier(null);
          setLevertijdForm({ levertijd_dagen: '' });
        }}
        title={`Levertijd instellen: ${editingLeverancier?.naam}`}
      >
        {editingLeverancier && (
          <form onSubmit={handleLevertijdSubmit} className="form">
            <div className="form-group">
              <label>Levertijd (dagen) *</label>
              <input
                type="number"
                min="0"
                max="365"
                value={levertijdForm.levertijd_dagen}
                onChange={(e) => setLevertijdForm({ levertijd_dagen: e.target.value })}
                required
                placeholder="Bijv. 7 voor 7 dagen"
              />
              <p className="form-help">
                Aantal werkdagen dat deze leverancier gemiddeld nodig heeft om te leveren.
              </p>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowLeverancierModal(false);
                  setEditingLeverancier(null);
                  setLevertijdForm({ levertijd_dagen: '' });
                }}
              >
                Annuleren
              </button>
              <button type="submit" className="btn btn-primary">
                <FiSave /> Opslaan
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* Parameter Edit Modal */}
      <Modal
        show={showParameterModal}
        onClose={() => {
          setShowParameterModal(false);
          setEditingParameter(null);
          setParameterForm({ parameter_naam: '', waarde: '', beschrijving: '' });
        }}
        title={editingParameter ? 'Parameter Bewerken' : 'Nieuwe Parameter'}
      >
        <form onSubmit={handleParameterSubmit} className="form">
          <div className="form-group">
            <label>Parameter Naam *</label>
            <input
              type="text"
              value={parameterForm.parameter_naam}
              onChange={(e) => setParameterForm({ ...parameterForm, parameter_naam: e.target.value })}
              required
              placeholder="Bijv. inkoopvoorstel_buffer_dagen"
              disabled={!!editingParameter}
            />
            <p className="form-help">
              Voor inkoopvoorstel parameters, begin met "inkoopvoorstel_"
            </p>
          </div>

          <div className="form-group">
            <label>Waarde</label>
            <textarea
              value={parameterForm.waarde}
              onChange={(e) => setParameterForm({ ...parameterForm, waarde: e.target.value })}
              placeholder="Bijv. 14 of JSON"
              rows={8}
              className="parameter-waarde-textarea"
            />
            <p className="form-help">
              Tekst, getal of JSON. Controleer JSON op geldige syntax voordat je opslaat.
            </p>
          </div>

          <div className="form-group">
            <label>Beschrijving</label>
            <textarea
              value={parameterForm.beschrijving}
              onChange={(e) => setParameterForm({ ...parameterForm, beschrijving: e.target.value })}
              rows="3"
              placeholder="Beschrijving van wat deze parameter doet..."
            />
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setShowParameterModal(false);
                setEditingParameter(null);
                setParameterForm({ parameter_naam: '', waarde: '', beschrijving: '' });
              }}
            >
              Annuleren
            </button>
            <button type="submit" className="btn btn-primary">
              <FiSave /> Opslaan
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Parameters;
